/**
 * TestStage — co-located body for onboarding step 6 (Test it).
 *
 * A safe, on-brand miniature of the live-agent experience for the wizard. There
 * is no persisted genie/session yet (backend PARKED), so this runs a fully
 * client-side, scripted "session" that never crashes if no agent exists:
 *
 *  - concentric brand rings + animated GenieDisc avatar (reuses the real
 *    `GenieAvatar` via `GenieDisc`, tinted from the draft genie type)
 *  - a status line that mirrors live-agent states (idle / connecting / listening)
 *  - the gradient Start / End conversation pill
 *  - a 3-control action row built on the Phase 0 `ActionBtn` atom
 *  - a scripted transcript that reveals as the genie "speaks"
 *
 * Brand law: genie tokens + signature gradient only; *Voice* tone; no "AI"
 * language; no em dashes. STUB session until BE lands.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { ActionBtn, GenieDisc } from "@/components/voice-genie/atoms";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";

type SessionState = "idle" | "connecting" | "listening";

interface TranscriptLine {
  who: "genie" | "caller";
  text: string;
}

/** Scripted demo exchange. Personalised with the draft business name when set. */
const buildTranscript = (businessName: string): TranscriptLine[] => {
  const name = businessName.trim() || "your business";
  return [
    { who: "genie", text: `Kia ora, you have reached ${name}. How can I help?` },
    { who: "caller", text: "Do you have any appointments on Tuesday?" },
    { who: "genie", text: "Let me check the calendar for you. Tuesday looks open from 10am." },
  ];
};

interface TestStageProps {
  /** Lifts whether the owner ran a test up to the page so it can persist to context. */
  onTested?: () => void;
}

export const TestStage = ({ onTested }: TestStageProps) => {
  const { draft } = useOnboarding();

  const businessName = draft.business?.name ?? "";
  const transcript = useMemo(() => buildTranscript(businessName), [businessName]);

  // Tint the avatar from the chosen genie type, falling back to the signature blue.
  const brandColor = "#4E9CFF";

  const [session, setSession] = useState<SessionState>("idle");
  const [revealed, setRevealed] = useState(0);
  const [micMuted, setMicMuted] = useState(true);
  const [genieMuted, setGenieMuted] = useState(false);

  const timers = useRef<number[]>([]);
  const clearTimers = () => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  };

  useEffect(() => () => clearTimers(), []);

  const startSession = () => {
    clearTimers();
    setRevealed(0);
    setSession("connecting");
    onTested?.();
    // connecting -> listening, then reveal scripted lines one at a time.
    timers.current.push(
      window.setTimeout(() => setSession("listening"), 900),
    );
    transcript.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(() => setRevealed(i + 1), 1500 + i * 1400),
      );
    });
  };

  const endSession = () => {
    clearTimers();
    setSession("idle");
    setRevealed(0);
  };

  const isLive = session !== "idle";
  const listening = session === "listening";

  const status =
    session === "connecting"
      ? "Connecting..."
      : session === "listening"
        ? "Listening..."
        : "Tap to talk to your genie";

  const visibleLines = transcript.slice(0, revealed);

  return (
    <div
      data-hg-block="test-stage"
      className="w-full flex flex-col items-center gap-6"
    >
      {/* status */}
      <p
        className={`text-[15px] font-medium transition-colors ${
          listening ? "text-genie-primary" : "text-[#64748B]"
        }`}
      >
        {status}
      </p>

      {/* avatar — concentric brand rings + animated disc */}
      <button
        type="button"
        onClick={isLive ? endSession : startSession}
        data-hg-block="genie-avatar"
        aria-label={isLive ? "End conversation" : "Start conversation"}
        className="relative flex items-center justify-center w-[150px] h-[150px] rounded-full focus:outline-none"
      >
        <span className="absolute inset-0 rounded-full border border-genie-secondary/20" />
        <span className="absolute w-[124px] h-[124px] rounded-full border border-genie-primary/25" />
        {listening && (
          <span className="absolute inset-0 rounded-full border border-genie-primary/30 animate-ping" />
        )}
        <span
          className="flex items-center justify-center w-[108px] h-[108px] rounded-full bg-genie-primary/10 overflow-hidden transition-shadow"
          style={{
            boxShadow: isLive
              ? "0 0 34px rgba(78,156,255,0.35)"
              : "0 0 18px rgba(78,156,255,0.18)",
          }}
        >
          <GenieDisc size={104} color={brandColor} />
        </span>
      </button>

      {/* start / end pill */}
      <button
        type="button"
        onClick={isLive ? endSession : startSession}
        className="h-[46px] px-6 rounded-xl text-[14.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/40 hover:opacity-90 transition-all"
      >
        {isLive ? "End conversation" : "Start conversation"}
      </button>

      {/* action row — reuses the Phase 0 ActionBtn atom */}
      <div
        data-hg-block="call-controls"
        className="flex flex-row items-start gap-5"
      >
        <ActionBtn
          icon={micMuted ? MicOff : Mic}
          label={micMuted ? "Unmute" : "Mute"}
          emphasis={micMuted ? "active" : "default"}
          disabled={!isLive}
          onClick={() => setMicMuted((m) => !m)}
        />
        <ActionBtn
          icon={MessageSquare}
          label="Type"
          emphasis="gradient"
          disabled={!isLive}
        />
        <ActionBtn
          icon={genieMuted ? VolumeX : Volume2}
          label={genieMuted ? "Unmute genie" : "Mute genie"}
          emphasis={genieMuted ? "active" : "default"}
          disabled={!isLive}
          onClick={() => setGenieMuted((m) => !m)}
        />
      </div>

      {/* mini transcript */}
      <div
        data-hg-block="transcript"
        className="w-full max-w-[420px] flex flex-col gap-2 mt-1"
      >
        {visibleLines.length === 0 ? (
          <p className="text-center text-[13px] text-gray-400">
            Your conversation will appear here as you talk.
          </p>
        ) : (
          visibleLines.map((m, i) => {
            const isGenie = m.who === "genie";
            return (
              <div
                key={i}
                className={`flex ${isGenie ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`max-w-[85%] p-2.5 rounded-[10px] text-[13px] leading-relaxed ${
                    isGenie
                      ? "bg-genie-primary text-white shadow-md mr-6"
                      : "bg-white text-[#111827] border border-genie-border shadow-sm ml-6"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TestStage;
